/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    GUEST_ACCESS_BR16_ADMIN_VISITORS
 * Date:         2026-07-06
 * Modified By:  Engineering
 *
 * Description:
 * BR-16 admin Visitors screen. Lists anonymous guest visitors (guest_visits) with
 * country / channel filters and paging, and lets an admin block / unblock an IP or
 * country (enforced on the /public guest endpoints via blockCheck).
 */

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getGuestVisits,
  blockGuest,
  unblockGuest,
  GuestVisitRow,
} from "../../../services/admin/guestVisitsApi";

const LIMIT = 50;

const GuestVisitors: React.FC = () => {
  const [rows, setRows] = useState<GuestVisitRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [country, setCountry] = useState("");
  const [channel, setChannel] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (p = 1) => {
      setLoading(true);
      try {
        const res = await getGuestVisits({
          country: country.trim() || undefined,
          channel: channel.trim() || undefined,
          page: p,
          limit: LIMIT,
        });
        setRows(res.data.items || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 0);
        setPage(res.data.page || 1);
      } catch (e) {
        toast.error("Failed to load visitors");
      } finally {
        setLoading(false);
      }
    },
    [country, channel],
  );

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doBlock = async (type: "ip" | "country", value: string) => {
    if (!value) return;
    try {
      await blockGuest({ type, value });
      toast.success(`Blocked ${type}: ${value}`);
      load(page);
    } catch (e) {
      toast.error(`Failed to block ${type}`);
    }
  };

  const doUnblock = async (type: "ip" | "country", value: string) => {
    try {
      await unblockGuest({ type, value });
      toast.success(`Unblocked ${type}: ${value}`);
      load(page);
    } catch (e) {
      toast.error(`Failed to unblock ${type}`);
    }
  };

  const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "—");
  const geo = (r: GuestVisitRow) =>
    [r.city, r.country].filter(Boolean).join(", ") || "—";
  const chan = (r: GuestVisitRow) =>
    [r.utmSource, r.utmCampaign].filter(Boolean).join(" / ") || "—";

  return (
    <div className="container-fluid mt-4">
      <h1 className="u-H2-24 mb-1">Visitors</h1>
      <p className="u-P3-14 text-muted mb-3">
        Anonymous guest activity ({total} total). Block an abusive IP or a whole country —
        enforced immediately on the public guest endpoints.
      </p>

      {/* Filters */}
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div>
          <label className="u-P3-14 text-muted d-block mb-1">Country (ISO)</label>
          <input
            className="form-control form-control-sm"
            placeholder="e.g. US"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ maxWidth: 140 }}
          />
        </div>
        <div>
          <label className="u-P3-14 text-muted d-block mb-1">Channel (utmSource)</label>
          <input
            className="form-control form-control-sm"
            placeholder="e.g. email"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            style={{ maxWidth: 160 }}
          />
        </div>
        <button className="btn btn-dark btn-sm" onClick={() => load(1)} disabled={loading}>
          {loading ? "Loading…" : "Apply filters"}
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => {
            setCountry("");
            setChannel("");
            setTimeout(() => load(1), 0);
          }}
          disabled={loading}
        >
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr className="u-P3-14 text-muted">
              <th>IP</th>
              <th>Location</th>
              <th>Channel</th>
              <th className="text-end">Searches</th>
              <th className="text-end">Hits</th>
              <th>Last search</th>
              <th>Last seen</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">
                  No visitors match these filters.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r._id}>
                <td className="u-P3-14">{r.ip}</td>
                <td className="u-P3-14">{geo(r)}</td>
                <td className="u-P3-14">{chan(r)}</td>
                <td className="u-P3-14 text-end">{r.searchCount ?? 0}</td>
                <td className="u-P3-14 text-end">{r.hits ?? 0}</td>
                <td className="u-P3-14">{r.lastSearch || "—"}</td>
                <td className="u-P3-14">{fmt(r.lastSeen)}</td>
                <td>
                  {r.blocked ? (
                    <span className="badge bg-danger">Blocked</span>
                  ) : (
                    <span className="badge bg-success">Active</span>
                  )}
                </td>
                <td className="text-end">
                  {r.blocked ? (
                    <button
                      className="btn btn-outline-success btn-sm"
                      onClick={() => doUnblock("ip", r.ip)}
                    >
                      Unblock IP
                    </button>
                  ) : (
                    <div className="d-inline-flex gap-1">
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => doBlock("ip", r.ip)}
                      >
                        Block IP
                      </button>
                      {r.country && (
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => doBlock("country", r.country as string)}
                          title={`Block all visitors from ${r.country}`}
                        >
                          Block {r.country}
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => load(page - 1)}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="u-P3-14 text-muted">
            Page {page} of {pages}
          </span>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => load(page + 1)}
            disabled={page >= pages || loading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default GuestVisitors;
